import React from "react";
import type { FC, CSSProperties } from "react";

import cn from "classnames";

import {
    UNIBOX_ADDITIONAL_DISPLAY_NAME,
    UNIBOX_ADDITIONAL_TEST_ID,
} from "./vars";

import type { UniBoxAdditionalProps } from "./types";

import classes from "./UniBoxAdditional.styl";

/**
 * Контейнер, который может принимать в себя множество дочерних компонентов и формировать из них различные композиции поверх других элементов.
 *
 * Служит конструктором, в который можно положить как готовые предустановленные компоненты, так и локальные, не имеющие готовой интерпретации в ДС.
 *
 * Систематизирует динамические отступы и оффсеты вложенных элементов.
 *
 * Элементы UniBoxAdditional находятся в 9 слотах, могут перекрывать друг друга и выходить за пределы контейнера.
 *
 * **Важно:** для корректной работы UniBoxAdditional, родительскому компоненту необходимо задать position, отличный от static.
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?m=auto&node-id=6181-53153&t=CGbKD6mrS1ryh4Rw-1" target="_blank">Спека</a>
 * <br><br>
 * ### Дочерние компоненты
 *
 * | Компонент | Использование |
 * |-----------|----------|
 * | `<UniBoxAdditional.SlotTopLeft />` | Слот, помещающий элемент в верхнюю левую часть контейнера. |
 * | `<UniBoxAdditional.SlotTop />` | Слот, помещающий элемент в верхнюю часть контейнера. |
 * | `<UniBoxAdditional.SlotTopRight />` | Слот, помещающий элемент в верхнюю правую часть контейнера. |
 * | `<UniBoxAdditional.SlotLeft />` | Слот, помещающий элемент в левую часть контейнера. |
 * | `<UniBoxAdditional.SlotMiddle />` | Слот, помещающий элемент в центральную часть контейнера. |
 * | `<UniBoxAdditional.SlotRight />` | Слот, помещающий элемент в правую часть контейнера. |
 * | `<UniBoxAdditional.SlotBottomLeft />` | Слот, помещающий элемент в нижнюю левую часть контейнера. |
 * | `<UniBoxAdditional.SlotBottom />` | Слот, помещающий элемент в нижнюю часть контейнера. |
 * | `<UniBoxAdditional.SlotBottomRight />` | Слот, помещающий элемент в нижнюю правую часть контейнера. |
 */
export const UniBoxAdditional: FC<UniBoxAdditionalProps> = ({
    "data-tsid": tsid,
    width,
    height,
    children,
    customVarsStyles,
    ...props
}) => {
    const uniBoxStyles: CSSProperties = {
        "--unibox-additional-width": width || "100%",
        "--unibox-additional-height": height || "100%",
    };

    const uniboxAdditionalClassnames = cn(
        classes["uniBox-additional"],
        customVarsStyles,
    );

    return (
        <div
            {...props}
            style={uniBoxStyles}
            className={uniboxAdditionalClassnames}
            data-tsid={tsid || UNIBOX_ADDITIONAL_TEST_ID}
            data-uikit={UNIBOX_ADDITIONAL_DISPLAY_NAME}
        >
            {children}
        </div>
    );
};

UniBoxAdditional.displayName = UNIBOX_ADDITIONAL_DISPLAY_NAME;

export default UniBoxAdditional;
